/** @jsxImportSource @emotion/react */

import css from '@emotion/css/macro'
import PropTypes from 'prop-types'
import React from 'react'

import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { paragraphSmall, weightSemiBold } from '../css'
import RichTextRenderer from '../RichTextRenderer'

const GameSummaryCard = ({
  summary,
  title = 'GAME SUMMARY',
  isMobile = false,
}) => {
  if (!summary) {
    return null
  }

  return (
    <div
      css={css`
        align-self: stretch;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 8px;
        display: inline-flex;
        ${isMobile && 'margin-top: 16px;'}
      `}
    >
      {/* Header */}
      <div
        css={css`
          align-self: stretch;
          justify-content: flex-start;
          align-items: center;
          display: inline-flex;
        `}
      >
        <div
          css={css`
            ${paragraphSmall}
            ${weightSemiBold}
            justify-content: center;
            display: flex;
            flex-direction: column;
            color: ${colors.NEUTRAL_50};
            text-transform: uppercase;
            line-height: 16.8px;
            letter-spacing: 1.68px;
            word-wrap: break-word;
          `}
        >
          {title}
        </div>
      </div>

      {/* Content */}
      <div
        css={css`
          width: 100%;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
          gap: 16px;
          display: flex;
        `}
      >
        {/* Title/Headline */}
        {summary.title && (
          <div
            css={css`
              align-self: stretch;
              padding-top: 16px;
              color: ${colors.NEUTRAL_0};
              font-size: 24px;
              font-family: ${isCanlan
                ? "'Neue Plak Condensed', Arial, Helvetica, sans-serif"
                : "'Barlow Condensed', Arial, Helvetica, sans-serif"};
              font-weight: 600;
              line-height: 28.8px;
              word-wrap: break-word;

              .rich-text-content {
                font-family: inherit;
                font-size: inherit;
                font-weight: normal;
                line-height: inherit;
                letter-spacing: inherit;
                color: inherit;
              }

              .rich-text-content p,
              .rich-text-content h1,
              .rich-text-content h2,
              .rich-text-content h3,
              .rich-text-content a {
                margin: 0;
                font-family: inherit;
                font-size: inherit;
                line-height: inherit;
                letter-spacing: inherit;
                color: inherit;
              }
            `}
          >
            <RichTextRenderer content={summary.title} showReadMore={false} />
          </div>
        )}

        {/* Content/Summary text */}
        {summary.content && (
          <div
            css={css`
              align-self: stretch;
              color: ${colors.NEUTRAL_0};
              font-size: 16px;
              font-family: 'Inter', Arial, Helvetica, sans-serif;
              font-weight: 400;
              line-height: 23.2px;
              word-wrap: break-word;

              a {
                color: ${colors.PRIMARY};
                text-decoration: underline;

                &:hover {
                  color: ${colors.PRIMARY};
                  text-decoration: underline;
                }

                &:visited {
                  color: ${colors.PRIMARY};
                }
              }
            `}
          >
            <RichTextRenderer
              content={summary.content}
              truncateLength={2000}
              showReadMore={true}
              readMoreAlign='left'
            />
          </div>
        )}
      </div>
    </div>
  )
}

GameSummaryCard.propTypes = {
  summary: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
  }),
  title: PropTypes.string,
  isMobile: PropTypes.bool,
}

export default GameSummaryCard
